import { type MetricTranslations, type TranslationsInput } from '@statsbomb/kitbag-ai-summary';
import { useTranslation } from 'react-i18next';
import EN from '@/i18n/en';
import { getMetricTranslations } from '@/utils/translations';

// Reuses the app's existing i18next bundles so ai-summary's text never drifts
// from the rest of the UI. Only add a namespace here once it exists in i18n.json.
const AI_SUMMARY_NAMESPACES = ['metrics', 'aiSummary'] as const;

export const useAISummaryTranslations = (): TranslationsInput | undefined => {
  const { i18n } = useTranslation(AI_SUMMARY_NAMESPACES);

  // Not a runtime check - ties this cast to our own i18n.json shape (typeof EN) rather than
  // kitbag-ai-summary's untyped TranslationsInput, so a drift from EN's type surfaces at any
  // other call site that also relies on it. It does not verify the installed library version's
  // actual expected keys.
  const translations = AI_SUMMARY_NAMESPACES.reduce<Partial<TranslationsInput>>((acc, namespace) => {
    const bundle = i18n.getResourceBundle(i18n.language, namespace) as (typeof EN)[typeof namespace];
    if (bundle) acc[namespace] = bundle;
    return acc;
  }, {});

  return Object.keys(translations).length > 0 ? (translations as TranslationsInput) : undefined;
};

export const useAISummaryMetricTranslations = (): MetricTranslations => {
  const { t } = useTranslation('metrics');
  return Object.fromEntries(Object.keys(EN.metrics).map(key => [key, getMetricTranslations(t, key)]));
};
